import * as Joi from "joi";
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { GenPartial, momentTransformer } from 'typeormgen';
import Base from '../base.entity';
import EpisodicProp from '../episodic-props/episodic-prop.entity';

@Entity('props')
export default class Prop extends Base {
	static tableName = 'props';

	@PrimaryGeneratedColumn()
	id: number;

	@Column('varchar')
	mobile_id: string;

	@Column('int')
	prod_id: number;

	@Column('int')
	episodic_prop_id: number;

	@Column('int')
	char_id: number;

	@Column('text')
	name: string;

	@Column('text')
	brand: string;

	@Column('text')
	color: string;

	@Column('text')
	material: string;

	@Column('text')
	description: string;

	@Column('int')
	is_established: number;

	@Column('int')
	is_personal: number;

	@Column('int')
	is_asset: number;

	@Column('text')
	size: string;

	@Column('text')
	dimensions: string;

	@Column('decimal')
	actual_price: string;

	@Column('decimal')
	unit_price: string;

	@Column('int')
	prop_category_id: number;

	@Column('int')
	prop_status_id: number;

	@Column('int')
	purchase_type: number;

	@Column('text')
	purchase_source: string;

	@Column('text')
	sku: string;

	@Column({ type: 'datetime', transformer: momentTransformer })
	rental_check_out: Moment;

	@Column({ type: 'datetime', transformer: momentTransformer })
	rental_check_in: Moment;

	@Column('varchar')
	rental_order_number: string;

	@Column('varchar')
	rental_iss_id: string;

	@Column({ type: 'datetime', transformer: momentTransformer })
	mto_start: Moment;

	@Column({ type: 'datetime', transformer: momentTransformer })
	mto_end: Moment;

	@Column('text')
	item_count: string;

	@Column('text')
	notes: string;

	@Column('text')
	wrap_box: string;

	@Column('text')
	storage_location: string;

	@Column('varchar')
	disposition: string;

	@Column('int')
	root: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column('int')
	created_by: number;

	@ManyToOne((type) => EpisodicProp, (episodicProp) => episodicProp.props)
	@JoinColumn({
		name: 'episodic_prop_id',
	})
	episodicProp: EpisodicProp;

	static rules() {
		return {
			id: Joi.number().integer(),
			mobile_id: Joi.string().max(36).allow(null, ''),
			prod_id: Joi.number().integer(),
			episodic_prop_id: Joi.number().integer().allow(null),
			char_id: Joi.number().integer().allow(null),
			name: Joi.string().max(255).allow(null),
			brand: Joi.string().max(255).allow(null),
			color: Joi.string().max(255).allow(null),
			material: Joi.string().max(255).allow(null),
			description: Joi.string().max(255).allow(null),
			is_established: Joi.number().integer().allow(null),
			is_personal: Joi.number().integer(),
			is_asset: Joi.number().integer(),
			size: Joi.string().max(255).allow(null),
			dimensions: Joi.string().max(255).allow(null),
			actual_price: Joi.string().max(11).allow(null),
			unit_price: Joi.string().max(11).allow(null),
			prop_category_id: Joi.number().integer().allow(null),
			prop_status_id: Joi.number().integer().allow(null),
			purchase_type: Joi.number().integer().allow(null),
			purchase_source: Joi.string().max(255).allow(null),
			sku: Joi.string().max(255).allow(null),
			rental_check_out: Joi.date().allow(null),
			rental_check_in: Joi.date().allow(null),
			rental_order_number: Joi.string().max(50).allow(null),
			rental_iss_id: Joi.string().max(50).allow(null),
			mto_start: Joi.date().allow(null),
			mto_end: Joi.date().allow(null),
			item_count: Joi.string().max(255).allow(null),
			notes: Joi.string().max(255).allow(null),
			wrap_box: Joi.string().max(255).allow(null),
			storage_location: Joi.string().max(255).allow(null),
			disposition: Joi.string().max(100).allow(null),
			root: Joi.number().integer(),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			date_created: Joi.date().allow(null),
			created_by: Joi.number().integer(),
		};
	}

	toJSON() {
		return {
			id: this.id,
			mobile_id: this.mobile_id,
			prod_id: this.prod_id,
			episodic_prop_id: this.episodic_prop_id,
			char_id: this.char_id,
			name: this.name,
			brand: this.brand,
			color: this.color,
			material: this.material,
			description: this.description,
			is_established: this.is_established,
			is_personal: this.is_personal,
			is_asset: this.is_asset,
			size: this.size,
			dimensions: this.dimensions,
			actual_price: this.actual_price,
			unit_price: this.unit_price,
			prop_category_id: this.prop_category_id,
			prop_status_id: this.prop_status_id,
			purchase_type: this.purchase_type,
			purchase_source: this.purchase_source,
			sku: this.sku,
			rental_check_out: this.rental_check_out,
			rental_check_in: this.rental_check_in,
			rental_order_number: this.rental_order_number,
			rental_iss_id: this.rental_iss_id,
			mto_start: this.mto_start,
			mto_end: this.mto_end,
			item_count: this.item_count,
			notes: this.notes,
			wrap_box: this.wrap_box,
			storage_location: this.storage_location,
			disposition: this.disposition,
			root: this.root,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			date_created: this.date_created,
			created_by: this.created_by,
		};
	}

	assign(props?: GenPartial<Prop>) {
		super.assign(props);

		if (props) {
			if (typeof props.rental_check_out !== 'undefined') {
				this.rental_check_out = momentTransformer.from(props.rental_check_out);
			}
			if (typeof props.rental_check_in !== 'undefined') {
				this.rental_check_in = momentTransformer.from(props.rental_check_in);
			}
			if (typeof props.mto_start !== 'undefined') {
				this.mto_start = momentTransformer.from(props.mto_start);
			}
			if (typeof props.mto_end !== 'undefined') {
				this.mto_end = momentTransformer.from(props.mto_end);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<Prop>) {
		super(props);
	}
}
